import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import FormActionButtons from "../Assets/FormActionButtons";
import { DosageEntry } from "../Types/Types";
import ImageUpload from "../Assets/ImageUpload";
import LanguageTabs, { FormLang } from "../Assets/LanguageTabs";
import { useTranslation } from "react-i18next";

interface DosageEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: string, formData: FormData) => void;
  initialData: DosageEntry;
  lang: string;
}

type Multi = { en: string; mk: string; sq: string };

const toMulti = (field: { [key: string]: string } | undefined): Multi => ({
  en: field?.en || "",
  mk: field?.mk || "",
  sq: field?.sq || "",
});

const DosageEditModal: React.FC<DosageEditModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}) => {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState<FormLang>("en");
  const [name, setName] = useState<Multi>(toMulti(initialData.name));
  const [text, setText] = useState<Multi>(toMulti(initialData.text));
  const [image, setImage] = useState<File | null>(null);

  useEffect(() => {
    setName(toMulti(initialData.name));
    setText(toMulti(initialData.text));
  }, [initialData]);

  const setField = (
    setter: React.Dispatch<React.SetStateAction<Multi>>,
    value: string
  ) => setter((prev) => ({ ...prev, [activeLang]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", JSON.stringify(name));
    formData.append("text", JSON.stringify(text));
    if (image) {
      formData.append("image", image);
    }
    onSubmit(initialData._id, formData);
    onClose();
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header", "bold", "italic", "underline", "strike",
    "list", "bullet", "link",
  ];

  if (!isOpen) return null;

  const langSuffix = activeLang.toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto font-montserrat">
        <h2 className="text-2xl font-bold mb-4 text-center text-brand-800">{t(`forms.editDosage`)}</h2>

        <LanguageTabs active={activeLang} onChange={setActiveLang} />

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-2">{t(`forms.name`)} ({langSuffix})</label>
            <input
              type="text"
              value={name[activeLang]}
              onChange={(e) => setField(setName, e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
              required={activeLang !== "sq"}
            />
          </div>
          <div>
            <label className="block mb-2">{t(`forms.text`)} ({langSuffix})</label>
            <ReactQuill
              key={`text-${activeLang}`}
              value={text[activeLang]}
              onChange={(value) => setField(setText, value)}
              theme="snow"
              className="h-48 mb-2"
              modules={quillModules}
              formats={quillFormats}
            />
          </div>
          <br />
          <br />
          <ImageUpload
            onImageSelect={(file) => setImage(file)}
            initialImage={!image ? initialData.image : undefined}
          />
          <FormActionButtons
            edit={true}
            onClose={onClose}
            onSubmit={handleSubmit}
            disabled={!name.en || !text.en || !name.mk || !text.mk}
          />
        </form>
      </div>
    </div>
  );
};

export default DosageEditModal;
